import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { didChangeState, type OperationResult } from '@/shared/api/operationResult'

export interface OperationRunner {
  /** Oxirgi natija — `OperationResultAlert` ga beriladi */
  result: OperationResult | null
  running: boolean
  run: (action: () => Promise<OperationResult>) => Promise<OperationResult>
  clear: () => void
}

/**
 * Boshqaruv amallarini bir xil qoida bo'yicha bajaradi.
 *
 * IKKI NARSANI BIR JOYDA QOTIRADI, chunki ikkalasi ham unutishga oson va
 * ikkalasi ham jimgina noto'g'ri ishlaydi:
 *
 * 1. **`PARTIAL` da ham kesh yangilanadi.** Ko'p uchraydigan xato —
 *    `if (result.status === 'OK') invalidate(...)` deb yozish. U holda
 *    "bazada saqlandi, sessiyaga qo'llanmadi" holatida ekrandagi ma'lumot
 *    ESKIRGAN qoladi va operator o'zgarish umuman bo'lmagan deb o'ylaydi.
 *    To'g'ri shart — `status !== 'FAILED'` (`didChangeState`).
 *
 * 2. **Tashlangan xato ham natijaga aylanadi.** `OperationResult` qaytaradigan
 *    endpointlar 200 bilan javob beradi, lekin tarmoq uzilishi yoki 403
 *    baribir bo'lishi mumkin. Uni `catch` siz qoldirish "hech narsa
 *    bo'lmadi" degan taassurot berardi — shuning uchun u `FAILED` bo'lib
 *    ko'rsatiladi.
 *
 * @param invalidateKeys amaldan keyin bekor qilinadigan kesh prefikslari
 */
export function useOperationRunner(
  invalidateKeys: readonly (readonly unknown[])[] = [],
): OperationRunner {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [result, setResult] = useState<OperationResult | null>(null)
  const [running, setRunning] = useState(false)

  const run = useCallback(
    async (action: () => Promise<OperationResult>): Promise<OperationResult> => {
      setRunning(true)
      setResult(null)
      try {
        const outcome = await action()
        setResult(outcome)
        if (didChangeState(outcome)) {
          for (const key of invalidateKeys) {
            void queryClient.invalidateQueries({ queryKey: key })
          }
        }
        return outcome
      } catch (error) {
        const failure: OperationResult = {
          status: 'FAILED',
          message: translateError(error, t),
        }
        setResult(failure)
        return failure
      } finally {
        setRunning(false)
      }
    },
    // `invalidateKeys` har chizilishda yangi massiv bo'lishi mumkin, shuning
    // uchun uning O'ZGARUVCHAN havolasi bog'liqlikka qo'shilmaydi — kalitlar
    // chaqiruv joyida statik yoziladi va o'zgarmaydi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, t],
  )

  const clear = useCallback(() => setResult(null), [])

  return { result, running, run, clear }
}
